import moment from 'moment';

function threeMonthsAgoStart() {
  return moment().subtract(3, 'months').startOf('day').toDate();
}

function saleDateComparable(sale) {
  if (!sale?.date) return null;
  const d = sale.date instanceof Date ? sale.date : new Date(sale.date);
  return Number.isNaN(d.getTime()) ? null : d;
}

/**
 * eBay sales older than ~3 months: listing URLs are unreliable; use Sales History / sale doc instead
 * (same rule as CardSalesRow / CardSalesTableRow / native CardSalesView).
 */
export function isStaleEbaySale(sale) {
  if (!sale?.itemId || sale.platform !== 'eBay') return false;
  const d = saleDateComparable(sale);
  if (!d) return false;
  return d < threeMonthsAgoStart();
}

/**
 * Elasticsearch document id for Sales History `saleId` / SaleProfile (prefers `id`, else `ebay-` + itemId).
 */
export function saleDocumentIdForHistoryLink(sale) {
  if (sale?.id) return sale.id;
  if (!sale?.itemId) return null;
  const raw = String(sale.itemId);
  return raw.includes('ebay') ? raw : `ebay-${raw}`;
}

export function buildSalesHistoryWebPath(sale) {
  const saleId = saleDocumentIdForHistoryLink(sale);
  if (!saleId) return null;
  return `/sales-history?saleId=${encodeURIComponent(saleId)}`;
}

/**
 * URL for opening a sale in a new tab (eBay: nordt/rt/orig_cvip params, same as SaleProfile openSale).
 * Stale eBay sales open Sales History with saleId (see CardSalesRow openSale).
 * Skips eBay params for internal sales-history URLs (see CardSalesRow windowOpen).
 * @param {{ url?: string|null, platform?: string, itemId?: string, id?: string, date?: Date|string }} sale
 * @returns {string|null}
 */
export function buildExternalSaleOpenUrl(sale) {
  if (isStaleEbaySale(sale)) {
    return buildSalesHistoryWebPath(sale);
  }
  if (!sale?.url) return null;
  let saleUrl = sale.url;
  if (sale.platform === 'eBay' && !saleUrl.includes('sales-history')) {
    const sep = saleUrl.includes('?') ? '&' : '?';
    saleUrl += `${sep}nordt=true&rt=nc&orig_cvip=true`;
  }
  return saleUrl;
}

export const getDailySalesFromSalesSearch = (sales) => {
  const response = {
    dailySales: {},
    numSales: 0,
    newestSale: null,
  };

  if (!sales || sales.length === 0) {
    return response;
  }

  sales.forEach((sale) => {
    const saleDate = new Date(sale.date);
    const key = moment(saleDate).utcOffset(-7).format('MM/DD/YYYY');

    if (response.dailySales[key]) {
      response.dailySales[key].p = ((response.dailySales[key].p * response.dailySales[key].n) + sale.price) / (response.dailySales[key].n + 1);
      response.dailySales[key].n += 1;
    } else {
      response.dailySales[key] = {
        p: sale.price,
        n: 1,
      };
    }
  });

  const newestDate = new Date(sales[0].date);

  response.newestSale = { date: newestDate, price: response.dailySales[moment(newestDate).utcOffset(-7).format('MM/DD/YYYY')].p };
  response.numSales = sales.length;

  return response;
};
